import AddIcon from '@mui/icons-material/Add';
import { Button } from '@mui/material';
import { FC } from 'react';
import { useLocation } from 'react-router';
import { PageContainer } from '../../components/Layout/PageContainer';
import { SOURCING_SEARCH_MOCK_DATA } from './data/sourcingSearchMockData';
import { ContactsWebPanel } from './profile/ContactsWebPanel';
import { FinancingHistoryPanel } from './profile/FinancingHistoryPanel';
import { ForesightSummaryPanel } from './profile/ForesightSummaryPanel';
import { FundingEvolutionPanel } from './profile/FundingEvolutionPanel';
import { HighlightsPanel } from './profile/HighlightsPanel';
import { OverviewSidebar } from './profile/OverviewSidebar';
import { SourcingProfileHeader } from './profile/SourcingProfileHeader';
import { TrafficStatsRow } from './profile/TrafficStatsRow';
import { sourcingPreviewScreen } from './sourcing-preview-screen';
import { PREVIEW_SEARCH_PROMPT, PREVIEW_SOURCING_PROFILE } from './sourcing.fixtures';
import { SourcingGrid } from './SourcingGrid';
import { SourcingListsView } from './SourcingListsView';
import { sourcingPageContainerSx, SourcingPreview } from './SourcingPreview';
import { sourcingProfileLayoutCss } from './SourcingProfilePage';
import { SourcingToolbar } from './SourcingToolbar';
import { useSourcingListName } from './useSourcingListName';

const ignore = () => undefined;

const SourcingAgentResultsPreview: FC = () => (
  <PageContainer sx={sourcingPageContainerSx}>
    <SourcingToolbar
      count={SOURCING_SEARCH_MOCK_DATA.length}
      prompt={PREVIEW_SEARCH_PROMPT}
      onDownloadCsv={ignore}
      onSaveSearch={ignore}
    />
    <SourcingGrid
      entityType='company'
      rowData={[...SOURCING_SEARCH_MOCK_DATA]}
      pagination
      paginationPageSize={25}
    />
  </PageContainer>
);

/** A fictional company through the live profile header and panels; the add-to-list action is a stand-in. */
const SourcingProfilePreview: FC = () => {
  const data = PREVIEW_SOURCING_PROFILE;
  return (
    <div css={sourcingProfileLayoutCss.page} data-testid='sourcing-profile-preview'>
      <SourcingProfileHeader
        data={data}
        companyId={data.id}
        addToListAction={
          <Button variant='contained' color='secondary' startIcon={<AddIcon sx={{ fontSize: 16 }} />}>
            Add to List
          </Button>
        }
      />
      <div css={sourcingProfileLayoutCss.scrollArea}>
        <div css={sourcingProfileLayoutCss.trafficStats}>
          <TrafficStatsRow stats={data.trafficStats} />
        </div>
        <div css={sourcingProfileLayoutCss.body}>
          <div css={sourcingProfileLayoutCss.sidebar}>
            <OverviewSidebar data={data} />
          </div>
          <div css={sourcingProfileLayoutCss.main}>
            <ForesightSummaryPanel summary={data.foresightSummary} />
            <FundingEvolutionPanel data={data.fundingEvolution} webTrafficData={data.webTrafficEvolution} />
            <FinancingHistoryPanel rounds={data.fundingRounds} />
            <ContactsWebPanel data={data} />
            <HighlightsPanel highlights={data.highlights} />
          </div>
        </div>
      </div>
    </div>
  );
};

const SourcingListPreviewForRoute: FC<{ id?: string; savedSearch: boolean }> = ({ id, savedSearch }) => (
  <SourcingPreview savedSearch={savedSearch} name={useSourcingListName(id, savedSearch)} />
);

/** The sample Sourcing screen for the current route, so the frost follows the sub-nav. */
export const SourcingModulePreview: FC = () => {
  const { pathname } = useLocation();
  const screen = sourcingPreviewScreen(pathname);

  switch (screen.kind) {
    case 'list':
      return <SourcingListPreviewForRoute id={screen.id} savedSearch={false} />;
    case 'savedSearch':
      return <SourcingListPreviewForRoute id={screen.id} savedSearch />;
    case 'agentResults':
      return <SourcingAgentResultsPreview />;
    case 'profile':
      return <SourcingProfilePreview />;
    default:
      return <SourcingListsView />;
  }
};
